/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 */
import React from "react";
import CustomCard from "../../../../components/CustomCard";
import CustomTooltip from "../../../../components/CustomTooltip";
import { CustomCheckbox } from "../../../../components/CustomCheckbox";
import {
    IconCheckboxChecked,
    IconCheckboxUnchecked, IconChipMultiplePerson,
    IconCloseChip,
    IconHouse
} from "../../../../components/buttons/IconButtons";

/**
 * Assigned unit card: unit name and the number of residents living in it.
 *
 * @param {Object} props - Component properties
 * @param {string} props.keyId - Unique identifier key for the item
 * @param {Object} props.item - Unit data to display
 * @param {string|number} props.item[keyId] - Unique identifier value
 * @param {string} props.item.unitName - Unit name
 * @param {number} [props.item.totalResidents] - Residents assigned to the unit
 * @param {boolean} props.item.disabled - Indicates if the item is disabled
 * @param {boolean} [props.item.checked] - Indicates if the item is selected (checkbox state)
 * @param {Function} [props.onDelete] - Callback executed when the delete action is triggered
 * @param {Function} [props.handleCheckboxChange] - Optional handler for checkbox state changes
 * @param {JSX.Element} [props.actions] - Actions menu rendered on the top right corner of the card
 *
 * @returns {JSX.Element} Unit card component with selection and delete functionality
 */
const UnitAssignedCard = (
    {
        t,
        keyId,
        item,
        onDelete,
        handleCheckboxChange,
        actions
    }
) => {
    return (
        <CustomCard
            classes="w-100"
            classCard="card-border-8px"
            classContent={`background-shades-50`}
        >
            <div className="row">
                <div className={"d-flex justify-content-between p-0"}>
                    { handleCheckboxChange &&
                        <div className={"align-top mr-3"}>
                            <CustomCheckbox
                                id={'cb_uac_row_' + keyId}
                                checked={item?.checked}
                                onChange={(e) => {
                                    handleCheckboxChange({
                                        target: {
                                            [keyId]: item[keyId],
                                            checked: e.target.checked,
                                        },
                                    });
                                }}
                                disabled={item?.disabled}
                                icon={<IconCheckboxUnchecked className={"icon-color-general-950"}/>}
                                checkedIcon={<IconCheckboxChecked className={"icon-color-shades-800"}/>}
                                disableRipple={true}
                            />
                        </div>
                    }

                    <div className={"flex-grow-1 minw-0 mr-1 mt-2"}>
                        <div className="d-flex align-items-center justify-content-between">
                            <div className={`col-12 p-0`}>
                                <div className='d-flex align-items-center justify-content-start'>
                                    <div><IconHouse className="mr-8px mb-1" width={16} height={16} /></div>
                                    <div className={`${onDelete ? 'mr-5' : ''} flex-grow-1 minw-0 p-0`}>
                                        <CustomTooltip
                                            text={`${t('apt')} ${item?.name}`}
                                            classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis-1-row `}
                                            showIfTruncated={true}
                                        />
                                    </div>
                                    { onDelete &&
                                        <div className={"position-absolute r-0px t-negative-4px"}>
                                            <IconCloseChip
                                                className='icon-color-shades-400 icon-hover-shades-950'
                                                onClick={() => onDelete(item)}
                                            />
                                        </div>
                                    }
                                    { actions &&
                                        <div className={"flex-shrink-0 ml-2"}>{actions}</div>
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="border-bottom-shades-300 my-2" />

                        <div className='d-flex align-items-center justify-content-between '>
                            <div className={'col-12 pl-0'}>
                                <div className="d-flex align-items-center">
                                    <div><IconChipMultiplePerson className="mr-8px mb-1" width={16} height={16} /></div>
                                    <div className={"minw-0"}>
                                        <CustomTooltip
                                            text={`${item?.totalResident} ${t('occupants')}`}
                                            classes={`fs-14px font-color-general-950 text-ellipsis-1-row`}
                                            showIfTruncated={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </CustomCard>
    );
}

export default UnitAssignedCard;
